import React, { useEffect, useState } from "react";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import { useTranslation } from "react-i18next";
import ContentLoader from "../../pages/ContentLoader";
import { useDispatch, useSelector } from "react-redux";
import { fetchAgencyMarketing, setFilters } from "../../redux/Slices/AgencyMarketingSlice";
import { Link, useNavigate, useParams } from "react-router-dom";
import style from './agencymarketing.module.css';

const AgencyMarketing = () => {
    const { t, i18n } = useTranslation("global");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { subType } = useParams();

    const { agency_marketing, isLoading, pagination, filters } = useSelector(
        (state) => state.agency_marketing
    );

    const individualTypes = ["marketer", "engineer", "instructor"];
    const companyTypes = ["real_estate", "engineering", "educational", "tech"];

    const [page, setPage] = useState(1);
    useEffect(() => {
        let type = "";
        let filter = "";

        if (subType) {
            if (individualTypes.includes(subType)) {
                type = "individual";
                filter = subType;
            } else if (companyTypes.includes(subType)) {
                type = "company";
                filter = subType;
            }
        }

        dispatch(setFilters({ type, filter }));
        setPage(1);
    }, [subType, dispatch]);

    useEffect(() => {
        dispatch(fetchAgencyMarketing({ page, ...filters }));
    }, [dispatch, i18n.language, t, page, filters]);

    const handleTypeChange = (type) => {
        dispatch(setFilters({ type, filter: "" }));
        setPage(1);
        if (type === "") {
            navigate("/agency-marketing");
        }
    };

    const handleFilterChange = (filter) => {
        dispatch(setFilters({ ...filters, filter }));
        setPage(1);
        if (filter === "") {
            navigate("/agency-marketing");
        } else {
            navigate(`/agency-marketing/${filter}`);
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const activeFilterList =
        filters.type === "individual" ? individualTypes : filters.type === "company" ? companyTypes : [];

    return (
        <div className="bg_overlay">
            <Breadcrumb title={t("services.agencyMarketing")} />
            <div className="agency-marketing py-5 position-relative">
                <div className="container">
                    <div>
                        <h5 className="main-color fw-bold mb-3"><i className="bi bi-funnel text-sm"></i> {t("labels.filterBy")}</h5>
                    </div>
                    <div className="d-flex gap-2 mb-3">
                        <button
                            className={`btn rounded-5 px-4 btn-sm ${filters.type === "" ? "btn-primary" : "btn-outline-primary"
                                }`}
                            onClick={() => handleTypeChange("")}
                        >
                            {t("servicesPage.all")}
                        </button>
                        <button
                            className={`btn rounded-5 px-4 btn-sm ${filters.type === "individual" ? "btn-primary" : "btn-outline-primary"
                                }`}
                            onClick={() => handleTypeChange("individual")}
                        >
                            {t("navbar.3ayinAffiliate")}
                        </button>
                        <button
                            className={`btn rounded-5 px-4 btn-sm ${filters.type === "company" ? "btn-primary" : "btn-outline-primary"
                                }`}
                            onClick={() => handleTypeChange("company")}
                        >
                            {t("navbar.3ayinCompanies")}
                        </button>
                    </div>
                    {filters.type && (
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            {activeFilterList.map((item) => (
                                <button
                                    key={item}
                                    className={`btn rounded-5 px-4 btn-sm ${filters.filter === item ? "btn-success" : "btn-outline-success"
                                        }`}
                                    onClick={() => handleFilterChange(item)}
                                >
                                    {filters?.type === "individual" ? (
                                        t(`jobTitles.${item}`)
                                    ) : (
                                        t(`companyTypes.${item}`)
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                    <hr />
                    {isLoading ? (
                        <ContentLoader />
                    ) : agency_marketing?.length > 0 ? (
                        <div className="mt-5 mb-2">
                            <div className="row g-3">
                                {agency_marketing.map((user) => (
                                    <div className="col-xl-3 col-lg-4 col-md-6 col-12" key={user?.id}>
                                        <Link to={`/provider_profile/${user?.id}`} className="card rounded-4 border-0 shadow-sm my-4 text-center">
                                            <img
                                                src={user?.image}
                                                alt={user?.profile?.name}
                                                className={style.img}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = "/maskk.png";
                                                }}
                                            />
                                            <div className="card-body">
                                                <span className="badge border bg-light text-dark mb-2">
                                                    {user?.type === "individual"
                                                        ? t("labels.individual")
                                                        : t("labels.company")}
                                                </span>
                                                <h6 className="fw-bold">{user?.profile?.name}</h6>
                                                <p className="text-sm mb-1">{user?.email} <i className="bi bi-envelope main-color"></i> </p>
                                                <p className="text-sm mb-2">{user?.phone}</p>
                                                <button type="button" className={`btn rounded-5 px-4 text-white shadow-sm btn-sm ${style.card_button}`}>
                                                    {t("profile.serviceProvider")}
                                                </button>
                                            </div>
                                        </Link>
                                    </div>
                                ))}
                            </div>

                            {pagination && (
                                <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        disabled={pagination.current_page === 1}
                                        onClick={() => handlePageChange(pagination.current_page - 1)}
                                    >
                                        {t("labels.previous")}
                                    </button>
                                    <span>
                                        {t("labels.page")} {pagination.current_page} {t("labels.of")}{" "}
                                        {pagination.last_page}
                                    </span>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        disabled={pagination.current_page === pagination.last_page}
                                        onClick={() => handlePageChange(pagination.current_page + 1)}
                                    >
                                        {t("labels.next")}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="no_data bg-white py-5 border rounded-2 my-3 text-center">
                            <h5 className="mb-0 text-sm">{t("no_data_exists")}</h5>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AgencyMarketing;